import { useEffect, useRef, useState } from 'react'
import { Check, Ellipsis } from 'lucide-react'
import { Button } from '@shared/design-system/ui'
import type { ChartMode } from './gross-sales-series'
import * as styles from './gross-sales-chart.css'

const choices = [
  { value: 'cumulative', label: 'Acumulado' },
  { value: 'daily', label: 'Diário' },
] as const

export function ChartViewMenu({
  mode,
  onChange,
}: {
  mode: ChartMode
  onChange: (mode: ChartMode) => void
}) {
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const items = useRef<Array<HTMLButtonElement | null>>([])
  const close = () => {
    setOpen(false)
    trigger.current?.focus()
  }

  useEffect(() => {
    if (!open) return
    items.current[mode === 'cumulative' ? 0 : 1]?.focus()
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && !root.current?.contains(event.target)) setOpen(false)
    }
    document.addEventListener('pointerdown', outside)
    return () => document.removeEventListener('pointerdown', outside)
  }, [open, mode])

  return (
    <div ref={root} className={styles.menuContainer}>
      <Button
        ref={trigger}
        variant="text"
        aria-label={`Opções do gráfico, ${mode === 'cumulative' ? 'Acumulado' : 'Diário'}`}
        aria-haspopup="menu"
        aria-expanded={open}
        className={styles.menuTrigger}
        onClick={() => setOpen((value) => !value)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault()
            setOpen(true)
          }
        }}
      >
        <Ellipsis size={20} aria-hidden />
      </Button>
      {open ? (
        <div
          role="menu"
          aria-label="Visualização do gráfico"
          className={styles.menu}
          onKeyDown={(event) => {
            if (event.key === 'Escape' || event.key === 'Tab') {
              if (event.key === 'Escape') event.preventDefault()
              close()
              return
            }
            const index = items.current.findIndex((item) => item === document.activeElement)
            const next =
              event.key === 'ArrowDown'
                ? (index + 1) % choices.length
                : event.key === 'ArrowUp'
                  ? (index + choices.length - 1) % choices.length
                  : event.key === 'Home'
                    ? 0
                    : event.key === 'End'
                      ? 1
                      : null
            if (next !== null) {
              event.preventDefault()
              items.current[next]?.focus()
            }
          }}
        >
          {choices.map((choice, index) => (
            <button
              key={choice.value}
              ref={(element) => {
                items.current[index] = element
              }}
              type="button"
              role="menuitemradio"
              aria-checked={mode === choice.value}
              tabIndex={-1}
              className={styles.menuItem}
              onClick={() => {
                onChange(choice.value)
                close()
              }}
            >
              <span>{choice.label}</span>
              {mode === choice.value ? <Check size={16} aria-hidden /> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}
